import { useCurrencySelection } from "@/redux/profile/hooks/UseCurrencySelection";
import { SpaceValues } from "@/lib/utils/SpaceValues";

import { FppSearchFilter } from "@/features/search-filter/components/FppSearchFilter";
import {
  SearchFilterSelectorContext,
  SearchFilterSelectorContextType,
} from "@/features/search-filter/context/searchFilterSelectorContext";
import { createFppSearchFilterActions } from "@/features/search-filter/redux/reducer";
import { useStatisticsYearSelect } from "@/features/statistics/hooks/UseStatisticsYearSelector";
import { Box, Row, SeparatorLine, Space, Txt } from "@stenajs-webui/core";
import {
  Card,
  CardBody,
  CardHeader,
  FlatButton,
} from "@stenajs-webui/elements";
import { SearchFilterContext } from "@stenajs-webui/filter";
import { StandardTable } from "@stenajs-webui/grid";
import { StandardTableExcelExportButton } from "@stenajs-webui/grid-export";
import { useContext, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { statisticsByCustomerFilterSettings } from "@/features/statistics/config/StatisticsByCustomerFilterConfig";
import { createByCustomerTableConfig } from "@/features/statistics/config/StatisticsByCustomerTableConfig";

import { useFilterWorkspace } from "@/features/search-filter/hooks/UseFilterWorkspace";
import { transformConfigToExcelFormat } from "@/features/statistics/lib/statisticsUtil";
import { routeFactory } from "@/RouteFactory";
import { useNavigate } from "react-router";
import { useCustomerListQuery } from "@/features/statistics/hooks/useCustomerListQuery";
import { PageLayout } from "@/components/page-layout/PageLayout";
import { Helmet } from "react-helmet";
import { CustomerStatisticsListItem } from "../../types/customer.types";

export const StatisticsByCustomerListScreen: React.FC = () => {
  const navigate = useNavigate();
  const { year: negotiationYear } = useStatisticsYearSelect();
  const { currency } = useCurrencySelection();
  const { committedState, workspaceState } = useContext(
    SearchFilterSelectorContext
  ) as SearchFilterSelectorContextType;

  const dispatch = useDispatch();

  const { openFilter } = useFilterWorkspace();

  const { customerList, loading, error, fetchCustomerList } =
    useCustomerListQuery();

  const onSearch = () => {
    const { salesRegions, negotiationStatus, sellers, routePairs } =
      workspaceState.formModel;
    if (
      salesRegions.length > 0 ||
      sellers.length > 0 ||
      routePairs.length > 0
    ) {
      fetchCustomerList({
        variables: {
          filterOptions: {
            negotiationYear,
            currencyCode: currency,
            negotiationStatuses: negotiationStatus.map(
              (negotiationStatus) => negotiationStatus.code
            ),
            sellerIds: sellers.map((seller) => seller.code),
            salesRegionCodes: salesRegions.map((salesRegion) =>
              parseInt(salesRegion.code)
            ),
            routePairCodes: routePairs.map((routePair) => routePair.id),
          },
        },
      });
    }
  };

  useEffect(() => {
    onSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, negotiationYear]);

  const config = useMemo(
    () =>
      createByCustomerTableConfig(currency, (id) =>
        navigate(routeFactory.statistics.customerDetails(id))
      ),
    [currency, navigate]
  );

  const statisticsByCustomerItems: CustomerStatisticsListItem[] =
    customerList?.byFilter.items || [];

  return (
    <>
      <Helmet title="Customer - Statistics - FPP" />
      <PageLayout>
        <Card minWidth={"1140px"}>
          <CardHeader
            text={"Customer"}
            contentRight={
              <Row alignItems={"center"}>
                <Txt>Export to Excel </Txt>
                <StandardTableExcelExportButton
                  config={transformConfigToExcelFormat(config, currency)}
                  items={statisticsByCustomerItems}
                />
              </Row>
            }
          />
          <CardBody>
            <Row>
              <Box>
                <SearchFilterContext
                  state={workspaceState}
                  actions={createFppSearchFilterActions(
                    "statistics.statisticsByCustomer"
                  )}
                  dispatch={dispatch}
                >
                  <FppSearchFilter
                    settings={statisticsByCustomerFilterSettings}
                    onSearch={onSearch}
                  />
                </SearchFilterContext>
              </Box>
            </Row>
          </CardBody>
          <SeparatorLine />
          <StandardTable
            items={statisticsByCustomerItems}
            config={config}
            loading={loading}
            error={error}
            noItemsHeader={
              committedState.salesRegions.length ||
              committedState.routePairs.length ||
              committedState.salesRegions.length
                ? "Your search did not return any results"
                : "Search for customers"
            }
            noItemsContentRight={
              <FlatButton label={"Open filter"} onClick={() => openFilter()} />
            }
            noItemsLabel={
              committedState.salesRegions.length ||
              committedState.routePairs.length ||
              committedState.salesRegions.length
                ? "No customers found"
                : "Please select seller, region or route to search for customers"
            }
          />
          {statisticsByCustomerItems && !loading ? <SeparatorLine /> : null}
          <Space num={SpaceValues.TWENTYFOUR} vertical />
        </Card>
      </PageLayout>
    </>
  );
};
